import {
  SET_CHECKOUT_ORDER,
  CHANGE_CHECKOUT_DATA,
  CLEAR_CHECKOUT_DATA,
  SET_PAYMENT_DATA,
  POPULATE_VOUCHER_CODES,
  REMOVE_APPLIED_VOUCHER,
  CLEAR_GUEST_EMAIL,
  CHANGE_MAP_ADDRESS_PHONE,
  NUMBER_EN,
  MINIMUM_TWO_CHARS_EN,
  SET_STANDARD_DELIVERY_ADDRESS,
  SET_MAP_DELIVERY_ADDRESS,
  RESET_CARD_LIST,
  SET_CHECKOUT_REQUEST_DATA,
  SET_INSTORE_DELIVERY_ADDRESS,
  SET_CHECKOUT_COM_FORM_VALIDATION_STATUS,
  SET_CREDIT_CARDS,
  RESET_CREDIT_CARDS,
  SET_SELECTED_CARD_CVV,
  CLEAR_SELECTED_CARD_CVV,
  SET_BIN_CODE,
  CLEAR_BIN_CODE,
  SHOW_CHECKOUT_SUMMARY_LOADER,
  HIDE_CHECKOUT_SUMMARY_LOADER,
  SET_SUMMARY_RECALCULATION_IN_PROGRESS,
  SET_PAYMENT_STATUS_RESPONSE,
  SET_PROCCED_TO_PAYMENT_LOADING
} from "../constants";
import validator from "validator";
import get from "lodash/get";
import trim from "lodash/trim";

type ErrorDetail = {
  valid: boolean;
  validWhen: boolean;
  check: string;
  message: string;
  options?: {
    min?: number;
    max?: number;
  };
};

type AddressErrors = {
  phone: ErrorDetail;
  firstname: ErrorDetail;
};

type Address = {
  firstname: string;
  areaId: string;
  addressLine1: string;
  collectionPointId: string;
  phone: string;
  cityId: number;
  area: string;
  errors: AddressErrors;
  areaCode: string;
  site_id: string;
  lat: string;
  lng: string;
};

// TODO: Replace Record<string, any> with real data when create types for those fields.

type CheckoutState = {
  checkoutData: Record<string, any>;
  checkoutOrder: Record<string, any>;
  mapFilterData: Record<string, any>;
  mapDeliveryAddress: Address;
  standardDeliveryAddress: Record<string, any>;
  inStoreDeliveryAddress: Address;
  inStoreList: any[];
  isSavedCard: boolean;
  savedCardList: any[];
  selectedSavedCard: any | null;
  selectedMSavedCard: any | null;
  areaCode: string;
  checkoutComFormValidationStatus: boolean;
  creditCardList: any[];
  selectedCardCvv: string;
  binNo: string;
  showCheckoutSummaryLoader: boolean;
  summaryRecalculationInProgress: boolean;
  paymentStatusResponse: Record<string, any>;
  isPickUpFormValid: {
    storePickUp: boolean;
    selfPickUp: boolean;
  };
  VoucherCodes?: any[];
  isProceedToPaymentLoading: boolean;
};
const initialState: CheckoutState = {
  checkoutData: {},
  checkoutOrder: {},
  mapFilterData: {},
  mapDeliveryAddress: {
    firstname: "",
    areaId: "",
    addressLine1: "",
    collectionPointId: "",
    phone: "",
    cityId: 0,
    area: "",
    errors: {
      phone: {
        valid: true,
        validWhen: false,
        check: "isEmpty",
        message: NUMBER_EN
      },
      firstname: {
        valid: true,
        validWhen: true,
        check: "isByteLength",
        message: MINIMUM_TWO_CHARS_EN,
        options: {
          min: 2,
          max: undefined
        }
      }
    },
    areaCode: "",
    site_id: "",
    lat: "",
    lng: ""
  },
  standardDeliveryAddress: {},
  inStoreDeliveryAddress: {
    firstname: "",
    areaId: "",
    addressLine1: "",
    collectionPointId: "",
    phone: "",
    cityId: 0,
    area: "",
    errors: {
      phone: {
        valid: true,
        validWhen: false,
        check: "isEmpty",
        message: NUMBER_EN
      },
      firstname: {
        valid: true,
        validWhen: true,
        check: "isByteLength",
        message: MINIMUM_TWO_CHARS_EN,
        options: {
          min: 2,
          max: undefined
        }
      }
    },
    areaCode: "",
    site_id: "",
    lat: "",
    lng: ""
  },
  inStoreList: [],
  isSavedCard: false,
  savedCardList: [],
  selectedSavedCard: null,
  selectedMSavedCard: null,
  areaCode: "",
  checkoutComFormValidationStatus: false,
  creditCardList: [],
  selectedCardCvv: "",
  binNo: "",
  showCheckoutSummaryLoader: false,
  summaryRecalculationInProgress: false,
  paymentStatusResponse: {},
  isPickUpFormValid: {
    storePickUp: false,
    selfPickUp: false
  },
  isProceedToPaymentLoading: false
};

const setPaymentData = (state: CheckoutState) => ({
  ...state,
  checkoutData: {
    ...state.checkoutData,
    selectedPayment: [],
    useMyWallet: false
  }
});
const setCheckoutOrder = (state: CheckoutState, { apiResponse }) => ({
  ...state,
  checkoutOrder: apiResponse
});

const changeCheckoutData = (state: CheckoutState, { name, value }) => ({
  ...state,
  checkoutData: {
    ...state.checkoutData,
    [name]: value
  }
});

const clearCheckoutData = (state: CheckoutState) => ({
  ...state,
  checkoutData: {}
});

const resetCardList = (state: CheckoutState) => ({
  ...state,
  savedCardList: []
});

const selectedMSavedCardData = (state: CheckoutState, { selectedData }) => ({
  ...state,
  selectedMSavedCard: selectedData,
  selectedSavedCard: null
});

const handleGuestEmailRemoval = (state: CheckoutState) => ({
  ...state,
  checkoutData: { ...state.checkoutData, guestEmail: "" }
});

const populateVoucherCodes = (state: CheckoutState, { VoucherCodes }) => ({
  ...state,
  VoucherCodes
});

const removeAppliedVoucher = (state: CheckoutState) => ({
  ...state,
  checkoutData: {
    ...state.checkoutData,
    voucherCode: "",
    discount: 0,
    discount_type: "",
    voucherNo: ""
  }
});

const handleIntlInputTextChange = (state: CheckoutState, action) => {
  if (action.deliveryType === "SELF_PICKUP") {
    let transformedState = {
      ...state,
      mapDeliveryAddress: {
        ...state.mapDeliveryAddress,
        [action.name]: action.value
      }
    };
    if (transformedState.mapDeliveryAddress.errors[action.name]) {
      transformedState.mapDeliveryAddress.errors = {
        ...state.mapDeliveryAddress.errors,
        [action.name]: state.mapDeliveryAddress.errors[action.name]
          ? {
              ...state.mapDeliveryAddress.errors[action.name],
              touched: true,
              valid: state.mapDeliveryAddress.errors[action.name].check
                ? validator[state.mapDeliveryAddress.errors[action.name].check](
                    action.value ? action.value.toString() : "",
                    state.mapDeliveryAddress.errors[action.name].options
                  ) ===
                    state.mapDeliveryAddress.errors[action.name].validWhen &&
                  (typeof action.status !== "undefined" ? action.status : true)
                : true
            }
          : { touched: true }
      };
    }
    return transformedState;
  } else if (action.deliveryType === "STORE_PICKUP") {
    let transformedState = {
      ...state,
      inStoreDeliveryAddress: {
        ...state.inStoreDeliveryAddress,
        [action.name]: action.value
      }
    };
    if (transformedState.inStoreDeliveryAddress.errors[action.name]) {
      transformedState.inStoreDeliveryAddress.errors = {
        ...state.inStoreDeliveryAddress.errors,
        [action.name]: state.inStoreDeliveryAddress.errors[action.name]
          ? {
              ...state.inStoreDeliveryAddress.errors[action.name],
              touched: true,
              valid: state.inStoreDeliveryAddress.errors[action.name].check
                ? validator[
                    state.inStoreDeliveryAddress.errors[action.name].check
                  ](
                    action.value ? action.value.toString() : "",
                    state.mapDeliveryAddress.errors[action.name].options
                  ) ===
                    state.mapDeliveryAddress.errors[action.name].validWhen &&
                  (typeof action.status !== "undefined" ? action.status : true)
                : true
            }
          : { touched: true }
      };
    }
    return transformedState;
  }
};

const setStandardDeliveryAddress = (state: CheckoutState, action) => ({
  ...state,
  standardDeliveryAddress: action.address
});

const setMapDeliveryAddress = (state: CheckoutState, { address }) => ({
  ...state,
  mapDeliveryAddress: {
    ...state.mapDeliveryAddress,
    areaId: address.areaId,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    collectionPointId: address.collectionPointId,
    phone: address.phone,
    firstname: address.firstname,
    lastname: address.lastname,
    cityId: address.cityId,
    area: address.area?.trim(),
    extra_info: address.extra_info?.trim(),
    lat: address.lat,
    lng: address.lng,
    site_id: address.site_id
  }
});

const setInstoreDeliveryAddress = (state: CheckoutState, { address }) => ({
  ...state,
  inStoreDeliveryAddress: {
    ...state.inStoreDeliveryAddress,
    areaId: address?.bfl_area_id,
    addressLine1: trim(get(address, "addressLine1", "")),
    addressLine2: trim(get(address, "addressLine2", "")),
    collectionPointId: get(address, "collectionPointId"),
    phone: get(address, "phone", ""),
    firstname: get(address, "firstname", ""),
    lastname: get(address, "lastname", ""),
    cityId: get(address, "cityId", 0),
    area: get(address, "area", "").trim(),
    extra_info: get(address, "extra_info", "").trim(),
    lat: get(address, "lat", ""),
    lng: get(address, "lng", ""),
    site_id: get(address, "site_id", ""),
    station_name: trim(get(address, "station_name", "")),
    id: get(address, "id"),
    city: address?.city || ""
  }
});

const setCheckoutRequestData = (state: CheckoutState, action) => ({
  ...state,
  checkoutRequestData: action.checkoutRequestData
});

const setCheckoutComFormValidationStatus = (state: CheckoutState, action) => ({
  ...state,
  checkoutComFormValidationStatus: action.payload
});

const setCreditCards = (state: CheckoutState, action) => ({
  ...state,
  creditCardList: action.payload
});

const resetCreditCards = (state: CheckoutState) => ({
  ...state,
  creditCardList: initialState.creditCardList
});

const setSelectedCardCvv = (state: CheckoutState, action) => ({
  ...state,
  selectedCardCvv: action.payload
});

const clearSelectedCardCvv = (state: CheckoutState) => ({
  ...state,
  selectedCardCvv: ""
});

const setBinCode = (state: CheckoutState, action) => {
  return {
    ...state,
    binNo: action.binNo
  };
};

const clearBinCode = (state: CheckoutState) => ({
  ...state,
  binNo: ""
});

const showCheckoutSummaryLoadingIndicator = (state: CheckoutState) => ({
  ...state,
  showCheckoutSummaryLoader: true
});

const hideCheckoutSummaryLoadingIndicator = (state: CheckoutState) => ({
  ...state,
  showCheckoutSummaryLoader: false
});
const setSummaryRecalculationInProgress = (state: CheckoutState, action) => ({
  ...state,
  summaryRecalculationInProgress: action.payload
});

const setPaymentStatusResponse = (state: CheckoutState, action) => ({
  ...state,
  paymentStatusResponse: action.payload
});

const setProceedToPaymentLoading = (state: CheckoutState, action) => ({
  ...state,
  isProceedToPaymentLoading: action.payload
});

const checkoutReducer = (
  state: CheckoutState = initialState,
  action
): CheckoutState => {
  switch (action.type) {
    case SET_PAYMENT_DATA:
      return setPaymentData(state);
    case CHANGE_CHECKOUT_DATA:
      return changeCheckoutData(state, action);
    case CLEAR_CHECKOUT_DATA:
      return clearCheckoutData(state);
    case SET_CHECKOUT_ORDER:
      return setCheckoutOrder(state, action);
    case POPULATE_VOUCHER_CODES:
      return populateVoucherCodes(state, action);
    case REMOVE_APPLIED_VOUCHER:
      return removeAppliedVoucher(state);
    case CLEAR_GUEST_EMAIL:
      return handleGuestEmailRemoval(state);
    case CHANGE_MAP_ADDRESS_PHONE:
      return handleIntlInputTextChange(state, action);
    case SET_STANDARD_DELIVERY_ADDRESS:
      return setStandardDeliveryAddress(state, action);
    case SET_MAP_DELIVERY_ADDRESS:
      return setMapDeliveryAddress(state, action);
    case SET_INSTORE_DELIVERY_ADDRESS:
      return setInstoreDeliveryAddress(state, action);
    case RESET_CARD_LIST:
      return resetCardList(state);
    case SET_CHECKOUT_REQUEST_DATA:
      return setCheckoutRequestData(state, action);
    case SET_CHECKOUT_COM_FORM_VALIDATION_STATUS:
      return setCheckoutComFormValidationStatus(state, action);
    case SET_CREDIT_CARDS:
      return setCreditCards(state, action);
    case RESET_CREDIT_CARDS:
      return resetCreditCards(state);
    case SET_SELECTED_CARD_CVV:
      return setSelectedCardCvv(state, action);
    case CLEAR_SELECTED_CARD_CVV:
      return clearSelectedCardCvv(state);
    case SET_BIN_CODE:
      return setBinCode(state, action);
    case CLEAR_BIN_CODE:
      return clearBinCode(state);
    case SHOW_CHECKOUT_SUMMARY_LOADER:
      return showCheckoutSummaryLoadingIndicator(state);
    case HIDE_CHECKOUT_SUMMARY_LOADER:
      return hideCheckoutSummaryLoadingIndicator(state);
    case SET_SUMMARY_RECALCULATION_IN_PROGRESS:
      return setSummaryRecalculationInProgress(state, action);
    case SET_PAYMENT_STATUS_RESPONSE:
      return setPaymentStatusResponse(state, action);
    case SET_PROCCED_TO_PAYMENT_LOADING:
      return setProceedToPaymentLoading(state, action);

    default:
      return state;
  }
};

export default checkoutReducer;
